import {
  CircleCheck,
  CircleX,
  Clock,
  Hourglass,
  PencilLine,
  Trash2,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react'
import { useState, type KeyboardEvent, type ReactNode } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { orUnknown, tartiNoText } from '@/features/report/reconcile/format'
import { formatDuration, formatInteger } from '@/lib/format'
import {
  issueCodes,
  LONG_ONSITE_MS,
  type IssueCode,
  type ReconcileRecord,
  type RecordStatus,
} from '@/lib/kantar-dia'
import { messages } from '@/lib/messages'
import { cn } from '@/lib/utils'

const t = messages.reconcile

const STATUS_VARIANT = {
  error: 'destructive',
  warning: 'warning',
  onsite: 'secondary',
  waiting: 'outline',
  ok: 'success',
  deleted: 'outline',
} as const satisfies Record<RecordStatus, string>

/** Durum simgesi lucide'den: emoji platforma göre farklı çiziliyor ve rozet rengiyle uyuşmuyordu. */
const STATUS_ICON = {
  error: CircleX,
  warning: TriangleAlert,
  onsite: Hourglass,
  waiting: Clock,
  ok: CircleCheck,
  deleted: Trash2,
} as const satisfies Record<RecordStatus, LucideIcon>

export function StatusIcon({ status, className }: { status: RecordStatus; className?: string }) {
  const Icon = STATUS_ICON[status]
  return <Icon aria-hidden="true" className={className} />
}

export function StatusBadge({ status }: { status: RecordStatus }) {
  return (
    <Badge variant={STATUS_VARIANT[status]}>
      <StatusIcon status={status} />
      {t.status[status]}
    </Badge>
  )
}

/** Elle düzeltilmiş kaydın işareti; üzerine gelince kim düzeltti ve açıklama. */
export function OverrideMarker({ record }: { record: ReconcileRecord }) {
  const override = record.override
  if (!override) return null
  const label = t.override.marker(override.updatedByName ?? t.unknown)
  return (
    <span
      className="text-muted-foreground inline-flex items-center"
      title={`${label}\n${override.note}`}
      aria-label={label}
    >
      <PencilLine className="size-3.5" aria-hidden="true" />
    </span>
  )
}

/** Kaydın durumu; sahadaki aracın girişten beri süresi rozetin yanında, 2 saatten sonra kırmızı. */
export function RecordStatusCell({ record }: { record: ReconcileRecord }) {
  return (
    <span className="flex flex-wrap items-center gap-1">
      <StatusBadge status={record.status} />
      {record.override && <OverrideMarker record={record} />}
      {record.onsiteMs !== null && (
        <span
          className={cn(
            'text-xs font-medium tabular-nums',
            record.onsiteMs > LONG_ONSITE_MS ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {t.onsiteFor(formatDuration(record.onsiteMs))}
        </span>
      )}
    </span>
  )
}

/** H- hata, U- uyarı. Kod her zaman adıyla: yalnız "H-A" ne olduğunu anlatmıyordu. */
export function CodeBadge({ code }: { code: IssueCode }) {
  return (
    <Badge variant={code.startsWith('H-') ? 'destructive' : 'warning'}>
      {code} {t.codes[code]}
    </Badge>
  )
}

export function CodeBadges({ record }: { record: ReconcileRecord }) {
  const codes = issueCodes(record)
  if (codes.length === 0) return null
  return (
    <span className="flex flex-wrap gap-1">
      {codes.map((code) => (
        <CodeBadge key={code} code={code} />
      ))}
    </span>
  )
}

/**
 * Telefonda tablo satırı yerine kart: tek dokunuşla detay açılır. `children` kayda özgü
 * ek satırlar (yalnız satır içi öğeler; kart bir `button`).
 */
export function RecordCard({
  record,
  onOpen,
  children,
}: {
  record: ReconcileRecord
  onOpen: (record: ReconcileRecord) => void
  children?: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={() => onOpen(record)}
      className="border-border bg-surface rounded-surface shadow-resting hover:border-border-strong focus-visible:ring-ring flex w-full flex-col gap-1.5 border p-4 text-left text-sm transition-[border-color] duration-(--duration-fast) outline-none focus-visible:ring-2 motion-reduce:transition-none"
    >
      <span className="flex items-center justify-between gap-2">
        <span className="tabular font-medium">
          {record.kantarAdi} · {tartiNoText(record.tartino)}
        </span>
        <RecordStatusCell record={record} />
      </span>
      <span className="text-muted-foreground">{orUnknown(record.plaka)}</span>
      {/* Uzun firma adı kesilmesin: ayrı satırda, en fazla iki satır. */}
      <span className="line-clamp-2">{orUnknown(record.cari)}</span>
      {children}
      <CodeBadges record={record} />
    </button>
  )
}

/** Kart listesi; uzun listede sayfa şişmesin diye parça parça açılır. */
export function CardList<T>({
  items,
  render,
  pageSize = 30,
}: {
  items: T[]
  render: (item: T, index: number) => ReactNode
  pageSize?: number
}) {
  const [shown, setShown] = useState(pageSize)
  const rest = items.length - shown
  return (
    <div className="flex flex-col gap-2">
      {items.slice(0, shown).map((item, index) => render(item, index))}
      {rest > 0 && (
        <Button variant="outline" onClick={() => setShown((count) => count + pageSize)}>
          {t.mobile.more(formatInteger(Math.min(rest, pageSize)), formatInteger(rest))}
        </Button>
      )}
    </div>
  )
}

export function Section({
  title,
  action,
  children,
}: {
  title: ReactNode
  action?: ReactNode
  children: ReactNode
}) {
  return (
    // `shrink-0`: kaydırılan bir sütunda (detay paneli) bölüm daralıp içeriğini kesmesin.
    <Card className="shrink-0 gap-0 py-0">
      <CardHeader className="border-border flex items-center justify-between gap-2 border-b px-4 py-3">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {action}
      </CardHeader>
      <CardContent className="overflow-x-auto p-0">{children}</CardContent>
    </Card>
  )
}

/**
 * Küçük statik liste (DESIGN §5: `ui/table`). `numeric`: sağa hizalı kolon sıraları.
 * `onRowClick` verilirse satırlar fare ve klavyeyle (Enter) seçilebilir.
 */
export function SimpleTable({
  head,
  rows,
  numeric = [],
  onRowClick,
  selectedRow,
}: {
  head: string[]
  rows: ReactNode[][]
  numeric?: number[]
  onRowClick?: (index: number) => void
  selectedRow?: number
}) {
  const align = (index: number) => numeric.includes(index) && 'tabular text-right'
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {head.map((label, index) => (
            <TableHead key={label} className={cn(align(index))}>
              {label}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((cells, rowIndex) => (
          <TableRow
            key={rowIndex}
            data-state={selectedRow === rowIndex ? 'selected' : undefined}
            {...(onRowClick && {
              tabIndex: 0,
              className: 'cursor-pointer',
              onClick: () => onRowClick(rowIndex),
              onKeyDown: (event: KeyboardEvent) => {
                if (event.key === 'Enter') onRowClick(rowIndex)
              },
            })}
          >
            {cells.map((cell, cellIndex) => (
              <TableCell key={cellIndex} className={cn('whitespace-normal', align(cellIndex))}>
                {cell}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
